export type ClientPortalRole =
    | 'client_owner'
    | 'project_collaborator'
    | 'approver'
    | 'billing_contact'
    | 'viewer'
    | 'custom';

export type ClientPortalPermissionKey =
    | 'viewProjects'
    | 'viewTasks'
    | 'viewFiles'
    | 'uploadFiles'
    | 'viewMessages'
    | 'sendMessages'
    | 'viewApprovals'
    | 'respondApprovals'
    | 'viewQuotes'
    | 'respondQuotes'
    | 'viewInvoices'
    | 'payInvoices'
    | 'manageTeam';

export type ClientPortalPermissions = Record<ClientPortalPermissionKey, boolean>;

export type ClientPortalPermissionSource = {
    isPrimary?: boolean;
    portalRole?: string;
    portalPermissions?: Partial<ClientPortalPermissions> | null;
};

export const CLIENT_PORTAL_ROLE_OPTIONS: Array<{
    value: ClientPortalRole;
    label: string;
    description: string;
    inviteMessage: string;
}> = [
    {
        value: 'client_owner',
        label: 'Client owner',
        description: 'Full portal access, including billing, approvals, and future client team controls.',
        inviteMessage: 'review projects, approvals, quotes, invoices, files, and manage your team\'s portal access',
    },
    {
        value: 'project_collaborator',
        label: 'Project collaborator',
        description: 'Project delivery access only. No quotes, invoices, or approvals.',
        inviteMessage: 'review shared projects, collaborate on delivery, upload files, and stay close to updates',
    },
    {
        value: 'approver',
        label: 'Approver',
        description: 'Can review and respond to approval requests for shared projects.',
        inviteMessage: 'review shared work and respond to approval requests when decisions are needed',
    },
    {
        value: 'billing_contact',
        label: 'Billing contact',
        description: 'Can view and respond to quotes and invoices for shared projects.',
        inviteMessage: 'review quotes, manage invoices, and stay on top of billing activity',
    },
    {
        value: 'viewer',
        label: 'Viewer',
        description: 'Read-only project, task, and file visibility.',
        inviteMessage: 'view shared projects, tasks, and files in a read-only portal space',
    },
];

export const CLIENT_PORTAL_ROLE_LABELS = Object.fromEntries(
    CLIENT_PORTAL_ROLE_OPTIONS.map((role) => [role.value, role.label]),
) as Record<ClientPortalRole, string>;

const NO_PERMISSIONS: ClientPortalPermissions = {
    viewProjects: false,
    viewTasks: false,
    viewFiles: false,
    uploadFiles: false,
    viewMessages: false,
    sendMessages: false,
    viewApprovals: false,
    respondApprovals: false,
    viewQuotes: false,
    respondQuotes: false,
    viewInvoices: false,
    payInvoices: false,
    manageTeam: false,
};

export const CLIENT_PORTAL_ROLE_PERMISSIONS: Record<ClientPortalRole, ClientPortalPermissions> = {
    client_owner: {
        viewProjects: true,
        viewTasks: true,
        viewFiles: true,
        uploadFiles: true,
        viewMessages: true,
        sendMessages: true,
        viewApprovals: true,
        respondApprovals: true,
        viewQuotes: true,
        respondQuotes: true,
        viewInvoices: true,
        payInvoices: true,
        manageTeam: true,
    },
    project_collaborator: {
        ...NO_PERMISSIONS,
        viewProjects: true,
        viewTasks: true,
        viewFiles: true,
        uploadFiles: true,
        viewMessages: true,
        sendMessages: true,
    },
    approver: {
        ...NO_PERMISSIONS,
        viewProjects: true,
        viewTasks: true,
        viewFiles: true,
        uploadFiles: true,
        viewMessages: true,
        sendMessages: true,
        viewApprovals: true,
        respondApprovals: true,
    },
    billing_contact: {
        ...NO_PERMISSIONS,
        viewProjects: true,
        viewQuotes: true,
        respondQuotes: true,
        viewInvoices: true,
        payInvoices: true,
    },
    viewer: {
        ...NO_PERMISSIONS,
        viewProjects: true,
        viewTasks: true,
        viewFiles: true,
    },
    custom: {
        ...NO_PERMISSIONS,
    },
};

export function normalizeClientPortalRole(value: unknown, isPrimary = false): ClientPortalRole {
    if (isPrimary) {
        return 'client_owner';
    }

    const role = String(value || '').trim();
    return role in CLIENT_PORTAL_ROLE_PERMISSIONS ? role as ClientPortalRole : 'project_collaborator';
}

export function resolveClientPortalPermissions(source?: ClientPortalPermissionSource | null): ClientPortalPermissions {
    const role = normalizeClientPortalRole(source?.portalRole, source?.isPrimary === true);
    return {
        ...CLIENT_PORTAL_ROLE_PERMISSIONS[role],
        ...(source?.portalPermissions || {}),
    };
}

export function hasClientPortalPermission(
    source: ClientPortalPermissionSource | null | undefined,
    permission: ClientPortalPermissionKey,
): boolean {
    return resolveClientPortalPermissions(source)[permission] === true;
}

export function clientPortalRoleLabel(value: unknown, isPrimary = false): string {
    const role = normalizeClientPortalRole(value, isPrimary);
    return CLIENT_PORTAL_ROLE_LABELS[role] || CLIENT_PORTAL_ROLE_LABELS.project_collaborator;
}

export function clientPortalInviteMessage(value: unknown, isPrimary = false): string {
    const role = normalizeClientPortalRole(value, isPrimary);
    return CLIENT_PORTAL_ROLE_OPTIONS.find((option) => option.value === role)?.inviteMessage
        || 'access the parts of the portal that have been shared with you';
}
